import React, { useState } from 'react';
import {
  Button,
  Header,
  Icon,
  Message,
  Segment,
  Label,
  Grid,
  Divider
} from 'semantic-ui-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

// 根据文件扩展名获取语言
function getLanguageFromPath(path) {
  const ext = path.split('.').pop()?.toLowerCase();
  
  const languageMap = {
    'js': 'javascript',
    'jsx': 'jsx',
    'ts': 'typescript',
    'tsx': 'tsx',
    'py': 'python',
    'go': 'go',
    'java': 'java',
    'c': 'c',
    'cpp': 'cpp',
    'cc': 'cpp',
    'cxx': 'cpp',
    'h': 'c',
    'hpp': 'cpp',
    'html': 'html',
    'htm': 'html',
    'css': 'css',
    'scss': 'scss',
    'sass': 'sass',
    'json': 'json',
    'xml': 'xml',
    'yml': 'yaml',
    'yaml': 'yaml',
    'md': 'markdown',
    'markdown': 'markdown',
    'sh': 'bash',
    'bash': 'bash',
    'sql': 'sql',
    'php': 'php',
    'rb': 'ruby',
    'rs': 'rust',
    'kt': 'kotlin',
    'swift': 'swift',
    'dart': 'dart',
    'scala': 'scala',
    'r': 'r',
    'matlab': 'matlab',
    'm': 'matlab'
  };
  
  return languageMap[ext] || 'text';
}

// 格式化文件大小
function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// 获取文件类型标签
function getFileTypeLabel(path, isBinary) {
  if (isBinary) {
    return <Label color='red'>二进制文件</Label>;
  }
  
  const ext = path.split('.').pop()?.toLowerCase();
  const language = getLanguageFromPath(path);
  
  if (language !== 'text') {
    return <Label color='blue'>{language.toUpperCase()}</Label>;
  }
  
  return <Label>文本文件</Label>;
}

const FileViewer = ({ file, onBack }) => {
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [wrapLines, setWrapLines] = useState(false);

  if (!file) {
    return null;
  }

  const language = getLanguageFromPath(file.path);
  const lines = file.content ? file.content.split('\n') : [];

  return (
    <div>
      {/* 文件头部信息 */}
      <Segment>
        <Grid>
          <Grid.Row>
            <Grid.Column width={12}>
              <Header as='h3'>
                <Icon name='file' />
                <Header.Content>
                  {file.name}
                  <Header.Subheader>
                    <Icon name='folder open' />
                    {file.path}
                  </Header.Subheader>
                </Header.Content>
              </Header>
            </Grid.Column>
            <Grid.Column width={4} textAlign='right'>
              <Button onClick={onBack} basic>
                <Icon name='arrow left' />
                返回
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        
        <Divider />
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {getFileTypeLabel(file.path, file.is_binary)}
          <Label basic>
            <Icon name='info circle' />
            大小: {formatFileSize(file.size)}
          </Label>
          <Label basic>
            <Icon name='code branch' />
            分支: {file.ref}
          </Label>
          {!file.is_binary && file.content && (
            <Label basic>
              <Icon name='list' />
              行数: {lines.length}
            </Label>
          )}
        </div>
      </Segment>

      {/* 文件内容 */}
      {file.is_binary ? (
        <Segment placeholder>
          <Header icon>
            <Icon name='file outline' />
            二进制文件
          </Header>
          <Message info>
            <Message.Header>无法显示二进制文件内容</Message.Header>
            <p>
              此文件是二进制格式，无法在浏览器中显示。文件大小: {formatFileSize(file.size)}
            </p>
          </Message>
        </Segment>
      ) : !file.content ? (
        <Segment placeholder>
          <Header icon>
            <Icon name='file outline' />
            空文件
          </Header>
          <Message info>此文件没有内容</Message>
        </Segment>
      ) : (
        <div>
          {/* 工具栏 */}
          <Segment basic style={{ padding: '10px 0', margin: '0' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button
                size='small'
                basic
                toggle
                active={showLineNumbers}
                onClick={() => setShowLineNumbers(!showLineNumbers)}
              >
                <Icon name='list ol' />
                行号
              </Button>
              <Button
                size='small'
                basic
                toggle
                active={wrapLines}
                onClick={() => setWrapLines(!wrapLines)}
              >
                <Icon name='text width' />
                自动换行
              </Button>
              <div style={{ marginLeft: 'auto', color: '#666', fontSize: '14px' }}>
                语言: {language}
              </div>
            </div>
          </Segment>

          {/* 代码内容 */}
          <Segment style={{ padding: '0', margin: '0' }}>
            <SyntaxHighlighter
              language={language}
              style={tomorrow}
              showLineNumbers={showLineNumbers}
              wrapLines={wrapLines}
              wrapLongLines={wrapLines}
              customStyle={{
                margin: 0,
                borderRadius: 0,
                fontSize: '14px',
                lineHeight: '1.5'
              }}
              codeTagProps={{
                style: {
                  fontSize: '14px',
                  fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace'
                }
              }}
            >
              {file.content}
            </SyntaxHighlighter>
          </Segment>
        </div>
      )}
    </div>
  );
};

export default FileViewer; 